type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';
type SpinnerTone = 'brand' | 'white' | 'neutral';

interface LoadingSpinnerProps {
  message?: string;
  fullPage?: boolean;
  /** Spinner diameter. Defaults to `md` (matches the original centered spinner). */
  size?: SpinnerSize;
  /** Ring color. `white` for spinners inside colored buttons, `neutral` for subtle rows. */
  tone?: SpinnerTone;
  /** Render just the spinner — no centering wrapper, no message. Use inside buttons/inline rows. */
  inline?: boolean;
  /** Extra classes for the spinner element (e.g. `mr-2`, `inline-block`). */
  className?: string;
}

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  xs: 'w-4 h-4 border-2',
  sm: 'w-5 h-5 border-2',
  md: 'w-8 h-8 border-2',
  lg: 'w-12 h-12 border-4',
};

const TONE_CLASSES: Record<SpinnerTone, string> = {
  brand: 'border-gray-200 border-t-vein-red-500',
  white: 'border-vein-border border-t-white',
  neutral: 'border-gray-200 border-t-gray-500',
};

export const LoadingSpinner = ({
  message = 'Loading...',
  fullPage = false,
  size = 'md',
  tone = 'brand',
  inline = false,
  className = '',
}: LoadingSpinnerProps) => {
  const spinner = (
    <div
      className={`${SIZE_CLASSES[size]} ${TONE_CLASSES[tone]} rounded-full animate-spin ${className}`}
      aria-hidden="true"
    />
  );

  if (inline) return spinner;

  const wrapper = fullPage ? 'min-h-screen' : 'min-h-[200px]';
  return (
    <div className={`flex items-center justify-center ${wrapper}`}>
      <div className="flex items-center gap-3">
        {spinner}
        {message && <span className="text-sm text-gray-600 font-medium">{message}</span>}
      </div>
    </div>
  );
};
